/**
 * The needs-confirming rule and its wording, shared by the two surfaces that
 * chase a proposed allocation: the dashboard widget and the planning-board
 * banner.
 *
 * The **classification** is a mirror of the backend's
 * `services/backend-api/src/allocations/attention.util.ts`. The widget takes
 * its `severity` / `daysUntilStart` straight from the server, which is the
 * authority; the banner classifies client-side because the board must not fire
 * an extra request to draw it (AC-25). Keeping one implementation here means
 * the two can only disagree about the *window*, never the rule — see
 * {@link DEFAULT_APPROACHING_DAYS}.
 *
 * The **wording** is shared unconditionally: "started 3 days ago" has to read
 * the same on both surfaces or they look like two different alerts.
 */

import { isProposedAllocation } from './allocationStatus';

export type AllocationAttentionSeverity = 'overdue' | 'approaching';

export interface AllocationAttention {
  severity: AllocationAttentionSeverity;
  /** Whole UTC days from today to the start date; negative when overdue. */
  daysUntilStart: number;
}

/**
 * The window the **banner** judges "approaching" by.
 *
 * An admin can widen or narrow the real window via the
 * `settings.allocationConfirmation` system setting, which the dashboard widget
 * honours because the server applies it. The board cannot: reading the setting
 * needs `manage:settings` (which planners don't hold) and would be an added
 * request either way. So the banner uses the product default and *says so* in
 * its copy — a stated 14-day window can't silently disagree with a configured
 * one the way an unlabelled count would.
 */
export const DEFAULT_APPROACHING_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Midnight UTC on the day `d` falls in. */
function startOfUtcDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * Whole UTC days from `now` to `startDate`; negative once the start has passed,
 * `null` for an unparseable date. Exported because the Proposed tab words the
 * distance for *every* row, including the far-future ones
 * {@link classifyAllocationAttention} deliberately declines to flag.
 */
export function daysUntilStartUtc(startDate: Date, now: Date): number | null {
  const startMs = startOfUtcDay(startDate);
  if (Number.isNaN(startMs)) return null;
  return Math.round((startMs - startOfUtcDay(now)) / MS_PER_DAY);
}

/**
 * Whether a proposed allocation needs chasing, and how urgently. Returns `null`
 * when nothing is owed.
 *
 * Two properties carried over from the backend rule, for the same reasons:
 *
 * - **Whole UTC days, not elapsed milliseconds.** An allocation starting at
 *   09:00 today is "starts in 0 days" all day, not "started 0 days ago" from
 *   09:01 — comparing raw timestamps would make the copy flip mid-morning.
 * - **`startDate` only.** An allocation whose whole window has already passed is
 *   still overdue (AC-E1); that it also ended is what makes it the clearest
 *   signal something was forgotten.
 *
 * A non-finite or negative `approachingDays` is treated as 0 — fail closed,
 * because the alternative is flagging every proposed allocation on the books.
 */
export function classifyAllocationAttention(
  status: unknown,
  startDate: Date,
  now: Date,
  approachingDays: number,
): AllocationAttention | null {
  if (!isProposedAllocation(status)) return null;

  const daysUntilStart = daysUntilStartUtc(startDate, now);
  if (daysUntilStart === null) return null;

  if (daysUntilStart < 0) return { severity: 'overdue', daysUntilStart };

  const window =
    Number.isFinite(approachingDays) && approachingDays > 0 ? approachingDays : 0;
  return daysUntilStart <= window ? { severity: 'approaching', daysUntilStart } : null;
}

const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;

/**
 * The sentence fragment describing when the work starts — "started 3 days ago",
 * "starts today", "starts in 5 days". Reads as a clause, so a caller can prefix
 * the project name.
 */
export function allocationAttentionPhrase(
  severity: AllocationAttentionSeverity,
  daysUntilStart: number,
): string {
  if (severity === 'overdue') return `started ${days(Math.abs(daysUntilStart))} ago`;
  if (daysUntilStart <= 0) return 'starts today';
  return `starts in ${days(daysUntilStart)}`;
}

/**
 * Chip label + MUI colour for a severity. Red for overdue, amber for
 * approaching (SPEC §Attention rule) — the same two tones the sibling
 * "Allocations expiring soon" widget uses for its urgency chip, so a planner
 * reads the dashboard's two allocation cards with one vocabulary.
 */
export function allocationAttentionChip(
  severity: AllocationAttentionSeverity,
  daysUntilStart: number,
): { label: string; color: 'error' | 'warning' } {
  if (severity === 'overdue') return { label: 'Overdue', color: 'error' };
  return { label: daysUntilStart <= 0 ? 'Today' : `${daysUntilStart}d`, color: 'warning' };
}
